/* eslint-disable max-lines */
/* TODO: remove the above line and reduce the line count */

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { getAppointmentTypeTranslationMap, getCurrencySymbol, getDisplayAmount } from '../utils';
import ModalConfirmation from 'FRONTEND/components/Calendar/ModalConfirmation';
import { editSettings } from 'FRONTEND/reducers/expertSlice';
import { selectCalendarActor } from 'FRONTEND/selectors/calendarSelectors';
import { selectedExpertHasStripe } from 'FRONTEND/selectors/expertSelectors';
import { useAddConsultationCodeMutation, useDeleteConsultationCodeMutation, useEditConsultationCodeMutation, useGetConsultationCodesForUserQuery } from 'FRONTEND/utils/backendApi/consultationCodesApi';
import { BOOKABLE_APPOINTMENT_TYPE_LIST, DURATION_LIST } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';
import { getDurationSettingKey } from 'FRONTEND/utils/stringFormatting';

function UserCalendarSettings() {
  const dispatch = useDispatch();
  const actor = useSelector(selectCalendarActor);
  const hasStripe = useSelector(state => selectedExpertHasStripe(state, actor.id));
  const handleActorSettingsChange = (settingKey, settingValue) => {
    dispatch(editSettings({
      expertId: actor.id,
      settings: {
        [settingKey]: settingValue,
      },
      showSnackbar: false,
    }));
  };

  // API hooks
  const { data: consultationCodes, isLoading: isCodesLoading, isError: isCodesError } = useGetConsultationCodesForUserQuery(actor?.id, {
    skip: !actor?.id,
  });
  const [addConsultationCode, { isError: isAddError, isLoading: isAddLoading }] = useAddConsultationCodeMutation();
  const [editConsultationCode, { isError: isEditError, isLoading: isEditLoading }] = useEditConsultationCodeMutation();
  const [deleteConsultationCode, { isError: isDeleteError, isLoading: isDeleteLoading }] = useDeleteConsultationCodeMutation();

  // Handle add code
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const handleAddCode = () => {
    if (!amount || !description) {
      console.error('Fields empty');
      return;
    }
    addConsultationCode({
      userId: actor.id.toString(),
      amount: parseFloat(amount),
      description,
    });
    // Clear form after triggering mutation
    setAmount('');
    setDescription('');
  };

  // Handle edit code
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editState, setEditState] = useState({
    id: null,
    description: '',
    amount: '',
    isDefault: false,
    currency: 'EUR', // Default currency, can be changed based on locale
  });
  const handleEditClick = (code) => {
    const id = code.id;
    const amount = code.activeFeeAmount / 100;
    const description = code.description || '';

    setEditState({ id, description, amount, isDefault: code.isDefault, currency: code.activeCurrency });
    setEditDialogOpen(true);
  };
  const handleEditCode = () => {
    if (!editState.id) {
      return;
    }
    editConsultationCode({
      consultationCodeId: editState.id,
      amount: parseFloat(editState.amount),
      description: editState.description,
      isDefault: editState.isDefault,
      userId: actor.id,
    });
    setEditDialogOpen(false);
    setEditState({ id: null, description: '', amount: '', isDefault: false });
  };

  // Handle delete code
  const [rateToDelete, setRateToDelete] = useState(null);
  const handleDeleteDialogClose = async(isDeletionConfirmed) => {
    setRateToDelete(null);
    if (isDeletionConfirmed && rateToDelete) {
      setRateToDelete(null);
      deleteConsultationCode({
        consultationCodeId: rateToDelete,
        userId: actor.id,
      });
    }
  };

  const locale = useSelector((state) => state.user.locale);
  return (
    <Box sx={{ py: 2, px: 1, width: '100%' }}>
      <Typography
        variant='h4'
        component='span'
      >
        {i18n._('User settings')}&nbsp;-&nbsp;
      </Typography>
      <Typography
        component='span'
        variant='h4'
        color='textSecondary'
      >
        {actor?.alias}
      </Typography>
      <Box sx={{ mt: 1 }}>
        <Paper sx={{ p: 2, mt: 2, maxWidth: '600px' }}>
          <Typography variant='h5' gutterBottom>{i18n._('Appointment durations')}</Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: (theme) => theme.palette.action.selected }}>{i18n._('Type')}</TableCell>
                  <TableCell sx={{
                    backgroundColor: (theme) => theme.palette.action.selected,
                    minWidth: '150px',
                  }}
                  >{i18n._('Duration')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {BOOKABLE_APPOINTMENT_TYPE_LIST.map((appointmentType) => (
                  <TableRow
                    key={appointmentType}
                    sx={{
                      '&:nth-of-type(even)': {
                        backgroundColor: (theme) => theme.palette.action.hover,
                      },
                    }}
                  >
                    <TableCell>
                      <Typography>{getAppointmentTypeTranslationMap()[appointmentType]}</Typography>
                    </TableCell>
                    <TableCell>
                      <Select
                        variant='standard'
                        fullWidth
                        value={actor?.[getDurationSettingKey(appointmentType)]}
                        onChange={(event) => handleActorSettingsChange(getDurationSettingKey(appointmentType), event.target.value)}
                      >
                        {DURATION_LIST.map((duration) => (
                          <MenuItem key={duration} value={duration}><Typography>{`${duration} ${i18n._('minutes')}`}</Typography></MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        {hasStripe && (
          <>
            <Divider sx={{ my: 2 }} />
            <Paper sx={{ p: 2, mt: 2, maxWidth: '600px' }}>
              <Typography variant='h5' gutterBottom>{i18n._('Billing')}</Typography>
              <Stack spacing={2}>
                <Stack>
                  <Typography variant='h6'>{i18n._('Your rates')}</Typography>
                  {(isCodesLoading || isAddLoading || isEditLoading || isDeleteLoading) && <CircularProgress sx={{ alignSelf: 'center' }} />}
                  {!isCodesLoading && (
                    <List dense>
                      {consultationCodes.map((code) => (
                        <ListItem
                          key={code.id}
                          dense
                          secondaryAction={
                            <>
                              <IconButton aria-label='edit' color='primary' onClick={() => handleEditClick(code)}>
                                <EditIcon />
                              </IconButton>
                              <IconButton edge='end' aria-label='delete' color='error' onClick={() => setRateToDelete(code.id)}>
                                <DeleteIcon />
                              </IconButton>
                            </>
                          }
                        >
                          <ListItemText
                            primary={(
                              <>
                                {code.description}
                                {code.isDefault && (
                                  <Typography variant='caption' color='primary' sx={{ ml: 1 }}>{i18n._('Default')}</Typography>
                                )}
                              </>
                            )}
                            secondary={getDisplayAmount(code.activeFeeAmount, code.activeCurrency, locale)}
                            slotProps={{ primary: { sx: { display: 'inline-block' } } }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Stack>
                <Stack spacing={1}>
                  <Typography variant='h6'>{i18n._('Add a rate')}</Typography>
                  <Stack spacing={1}>
                    <TextField
                      variant='outlined'
                      size='small'
                      placeholder={i18n._('Label')}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                    <TextField
                      variant='outlined'
                      size='small'
                      type='number'
                      placeholder={i18n._('Amount')}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      slotProps={{
                        input: {
                          endAdornment: <InputAdornment position='end'>€</InputAdornment>, // TODO: Make currency selectable
                        },
                      }}
                    />
                    <Button
                      variant='contained'
                      sx={{ maxWidth: 'fit-content', alignSelf: 'center', px: 4 }}
                      onClick={handleAddCode}
                      disabled={!amount || !description || isCodesLoading}
                    >
                      {i18n._('Add')}
                    </Button>
                    {isCodesError && <Typography variant='subtitle1' color='error' textAlign='center'>{i18n._('An error occurred while fetching fees. Please try again later.')}</Typography>}
                    {isAddError && <Typography variant='subtitle1' color='error' textAlign='center'>{i18n._('Error adding rate. Please try again.')}</Typography>}
                    {isEditError && <Typography variant='subtitle1' color='error' textAlign='center'>{i18n._('Error editing rate. Please try again.')}</Typography>}
                    {isDeleteError && <Typography variant='subtitle1' color='error' textAlign='center'>{i18n._('Error deleting rate. Please try again.')}</Typography>}
                  </Stack>
                </Stack>
              </Stack>
            </Paper>
          </>
        )}
      </Box>

      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        sx={{ '& .MuiDialog-paper': { p: 1, width: '500px' }, ' & .MuiDialogContent-root': { pt: 1 } }}
      >
        <DialogTitle variant='h5'>{i18n._('Edit rate')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <TextField
              variant='outlined'
              size='small'
              placeholder={i18n._('Label')}
              value={editState?.description || ''}
              onChange={(e) => setEditState({ ...editState, description: e.target.value })}
            />
            <TextField
              variant='outlined'
              size='small'
              type='number'
              placeholder={i18n._('Amount')}
              value={editState?.amount || ''}
              onChange={(e) => setEditState({ ...editState, amount: e.target.value })}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position='end'>{getCurrencySymbol(locale, editState.currency)}</InputAdornment>,
                },
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editState?.isDefault || false}
                  onChange={(e) => setEditState({ ...editState, isDefault: e.target.checked })}
                  color='primary'
                  size='small'
                />
              }
              label={i18n._('Set as default')}
              labelPlacement='end'
            />
            <Button
              variant='contained'
              color='primary'
              sx={{ maxWidth: 'fit-content', alignSelf: 'center', px: 4 }}
              onClick={handleEditCode}
            >
              {i18n._('Save')}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
      <ModalConfirmation
        open={Boolean(rateToDelete)}
        onClose={handleDeleteDialogClose}
        message={i18n._('Are you sure you want to delete this rate? This action cannot be undone.')}
        title={i18n._('Delete rate')}
      />
    </Box>
  );
}

export default UserCalendarSettings;
